import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { cn } from "@/lib/utils";
import type { SchemaWarning } from "@/lib/sql-schema-validator";

interface SqlTextareaProps {
  value: string;
  onChange: (value: string) => void;
  tables: string[];
  columns: Record<string, string[]>;
  placeholder?: string;
  rows?: number;
  className?: string;
  schemaWarnings?: SchemaWarning[];
}

const SQL_KEYWORDS = [
  "SELECT", "FROM", "WHERE", "AND", "OR", "INSERT", "INTO", "VALUES",
  "UPDATE", "SET", "DELETE", "JOIN", "LEFT", "RIGHT", "INNER", "OUTER",
  "ON", "GROUP", "BY", "ORDER", "ASC", "DESC", "LIMIT", "OFFSET",
  "HAVING", "DISTINCT", "AS", "IN", "NOT", "NULL", "IS", "LIKE",
  "BETWEEN", "EXISTS", "UNION", "ALL", "COUNT", "SUM", "AVG", "MIN", "MAX",
  "CREATE", "ALTER", "DROP", "TABLE", "INDEX", "VIEW", "CASE", "WHEN", "THEN", "ELSE", "END",
];

const PLACEHOLDER_TYPES = [
  "dropdown",
  "multiselect",
  "autocomplete",
  "date",
  "number",
  "text",
  "datetime-local",
];

function getPlaceholderTypeContext(
  text: string,
  cursorPos: number,
): { partial: string; replaceStart: number; replaceEnd: number } | null {
  const before = text.slice(0, cursorPos);
  const lastOpen = before.lastIndexOf("{{");
  if (lastOpen === -1) return null;
  const segment = before.slice(lastOpen);
  if (segment.includes("}}")) return null;
  const pipeIndex = segment.indexOf("|");
  if (pipeIndex === -1) return null;

  const pipePos = lastOpen + pipeIndex;
  const partial = before.slice(pipePos + 1);
  if (!/^[\w-]*$/.test(partial)) return null;

  let replaceEnd = cursorPos;
  while (replaceEnd < text.length && /[\w-]/.test(text[replaceEnd])) replaceEnd++;

  return { partial, replaceStart: pipePos + 1, replaceEnd };
}

function getWordAtCursor(text: string, cursorPos: number): { word: string; start: number; end: number } {
  let start = cursorPos;
  let end = cursorPos;
  while (start > 0 && /[a-zA-Z0-9_.]/.test(text[start - 1])) start--;
  while (end < text.length && /[a-zA-Z0-9_.]/.test(text[end])) end++;
  return { word: text.slice(start, end), start, end };
}

/** Build highlighted HTML for the overlay that shows warnings */
function buildHighlightedHtml(value: string, invalidNames: Set<string>): string {
  if (invalidNames.size === 0) return "";

  // Escape HTML
  const escape = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  // Build regex to match any invalid name as a word boundary
  const pattern = new RegExp(
    `\\b(${[...invalidNames].map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})\\b`,
    "gi"
  );

  let result = "";
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(value)) !== null) {
    result += escape(value.slice(lastIndex, match.index));
    result += `<mark class="sql-warning-highlight">${escape(match[0])}</mark>`;
    lastIndex = pattern.lastIndex;
  }
  result += escape(value.slice(lastIndex));
  return result;
}

const SqlTextarea = ({
  value,
  onChange,
  tables,
  columns,
  placeholder,
  rows = 8,
  className,
  schemaWarnings = [],
}: SqlTextareaProps) => {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [cursorInfo, setCursorInfo] = useState<{ word: string; start: number; end: number }>({ word: "", start: 0, end: 0 });
  const [suggestionMode, setSuggestionMode] = useState<"sql" | "placeholder-type">("sql");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  // All column names flattened
  const allColumns = Object.values(columns).flat();
  // Qualified columns: table.column
  const qualifiedColumns: string[] = [];
  for (const [table, cols] of Object.entries(columns)) {
    for (const col of cols) {
      qualifiedColumns.push(`${table}.${col}`);
    }
  }

  const allSuggestions = [...new Set([...SQL_KEYWORDS, ...tables, ...allColumns, ...qualifiedColumns])];

  // Set of invalid names from schema warnings
  const invalidNames = useMemo(() => {
    const names = new Set<string>();
    for (const w of schemaWarnings) {
      names.add(w.name);
    }
    return names;
  }, [schemaWarnings]);

  const highlightedHtml = useMemo(
    () => buildHighlightedHtml(value, invalidNames),
    [value, invalidNames]
  );

  // Sync scroll between textarea and backdrop
  const handleScroll = useCallback(() => {
    if (textareaRef.current && backdropRef.current) {
      backdropRef.current.scrollTop = textareaRef.current.scrollTop;
      backdropRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  }, []);

  const computeSuggestions = useCallback(
    (word: string) => {
      if (!word || word.length < 1) return [];
      const lower = word.toLowerCase();
      
      if (lower.includes(".")) {
        const [tablePart] = lower.split(".");
        const tableCols = columns[tablePart] || [];
        const afterDot = lower.split(".").slice(1).join(".");
        return tableCols
          .filter((c) => c.toLowerCase().startsWith(afterDot))
          .map((c) => `${tablePart}.${c}`)
          .slice(0, 12);
      }

      return allSuggestions
        .filter((s) => s.toLowerCase().startsWith(lower) && s.toLowerCase() !== lower)
        .sort((a, b) => {
          const aIsTable = tables.includes(a);
          const bIsTable = tables.includes(b);
          const aIsCol = allColumns.includes(a);
          const bIsCol = allColumns.includes(b);
          if (aIsTable && !bIsTable) return -1;
          if (!aIsTable && bIsTable) return 1;
          if (aIsCol && !bIsCol) return -1;
          if (!aIsCol && bIsCol) return 1;
          return a.localeCompare(b);
        })
        .slice(0, 12);
    },
    [tables, columns, allColumns, allSuggestions]
  );

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    const pos = e.target.selectionStart || 0;

    const phCtx = getPlaceholderTypeContext(newValue, pos);
    if (phCtx) {
      const lower = phCtx.partial.toLowerCase();
      const matches = PLACEHOLDER_TYPES.filter((t) =>
        t.toLowerCase().startsWith(lower),
      );
      setCursorInfo({ word: phCtx.partial, start: phCtx.replaceStart, end: phCtx.replaceEnd });
      setSuggestions(matches);
      setShowSuggestions(matches.length > 0);
      setSelectedIndex(0);
      setSuggestionMode("placeholder-type");
      return;
    }

    setSuggestionMode("sql");
    const info = getWordAtCursor(newValue, pos);
    setCursorInfo(info);

    const matches = computeSuggestions(info.word);
    setSuggestions(matches);
    setShowSuggestions(matches.length > 0);
    setSelectedIndex(0);
  };

  const applySuggestion = (suggestion: string) => {
    const before = value.slice(0, cursorInfo.start);
    const after = value.slice(cursorInfo.end);
    const suffix = suggestionMode === "placeholder-type" ? "" : " ";
    const newValue = before + suggestion + suffix + after;
    onChange(newValue);
    setShowSuggestions(false);

    requestAnimationFrame(() => {
      if (textareaRef.current) {
        const pos = cursorInfo.start + suggestion.length + suffix.length;
        textareaRef.current.selectionStart = pos;
        textareaRef.current.selectionEnd = pos;
        textareaRef.current.focus();
      }
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Tab" || e.key === "Enter") {
      if (showSuggestions && suggestions.length > 0) {
        e.preventDefault();
        applySuggestion(suggestions[selectedIndex]);
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const handleBlur = () => {
    setTimeout(() => setShowSuggestions(false), 200);
  };

  useEffect(() => {
    if (suggestionsRef.current) {
      const selected = suggestionsRef.current.children[selectedIndex] as HTMLElement;
      selected?.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex]);

  const getCategory = (s: string) => {
    if (suggestionMode === "placeholder-type") return "type";
    if (tables.includes(s)) return "table";
    if (s.includes(".") || allColumns.includes(s)) return "column";
    return "keyword";
  };

  const hasWarnings = invalidNames.size > 0;

  return (
    <div className="relative">
      {/* Highlight backdrop — sits behind the textarea */}
      {hasWarnings && (
        <div
          ref={backdropRef}
          className="absolute inset-0 overflow-hidden pointer-events-none rounded-md px-3 py-2 text-sm font-mono text-xs whitespace-pre-wrap break-words text-transparent"
          aria-hidden
          dangerouslySetInnerHTML={{ __html: highlightedHtml }}
          style={{ lineHeight: "1.5" }}
        />
      )}
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onScroll={handleScroll}
        placeholder={placeholder}
        rows={rows}
        className={cn(
          "flex min-h-[80px] w-full rounded-md border border-input px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-mono text-xs",
          hasWarnings ? "bg-transparent" : "bg-background",
          className
        )}
      />
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 mt-1 max-h-48 w-72 overflow-y-auto rounded-md border border-border bg-popover shadow-md"
        >
          {suggestions.map((s, i) => {
            const cat = getCategory(s);
            return (
              <button
                key={s}
                onMouseDown={(e) => {
                  e.preventDefault();
                  applySuggestion(s);
                }}
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-accent",
                  i === selectedIndex && "bg-accent"
                )}
              >
                <span
                  className={cn(
                    "shrink-0 rounded px-1 py-0.5 text-[10px] font-medium uppercase",
                    cat === "type" && "bg-violet-500/15 text-violet-600 dark:text-violet-400",
                    cat === "table" && "bg-primary/15 text-primary",
                    cat === "column" && "bg-secondary text-secondary-foreground",
                    cat === "keyword" && "bg-muted text-muted-foreground"
                  )}
                >
                  {cat === "type" ? "TYPE" : cat === "table" ? "TBL" : cat === "column" ? "COL" : "SQL"}
                </span>
                <span className="font-mono truncate">{s}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SqlTextarea;
